'use client'
import { useState } from 'react'

import { useTranslation } from '@/hooks/client'
import SvgChevronDown from '@/icons/solid/ChevronDown'
import { tw } from '@/lib/utils'
import type { NavPanelConfig, NavTabConfig } from '@/types/nav-types'

interface LauncherTreeProps {
  panels: NavPanelConfig[]
  activePanelId?: string
  activeTabId?: string
  onSelectPanel: (panel: NavPanelConfig) => void
  onSelectTab: (tab: NavTabConfig) => void
}

// The section's map, one column: its panels (Prescription, Brands, ...) with
// the open panel's lists (Featured, Best in Noise, ...) nested under it, the
// chosen list on the band's one tinted block. Disclosure is separate from
// selection: collapsing or reopening the current panel keeps its list.
export const LauncherTree = ({ panels, activePanelId, activeTabId, onSelectPanel, onSelectTab }: LauncherTreeProps) => {
  const { t } = useTranslation()
  const [collapsedPanelId, setCollapsedPanelId] = useState<string>()

  const selectPanel = (panel: NavPanelConfig) => {
    if (panel.id === activePanelId) {
      setCollapsedPanelId((current) => (current === panel.id ? undefined : panel.id))
      return
    }
    setCollapsedPanelId(undefined)
    onSelectPanel(panel)
  }

  return (
    <nav
      aria-label={t('nav.sheet.categoriesAria', 'Categories')}
      className="-ml-3 flex w-[200px] shrink-0 flex-col self-start laptop:w-[224px] min-[1440px]:w-[272px]"
    >
      {panels.map((panel) => {
        const selected = panel.id === activePanelId
        const open = selected && collapsedPanelId !== panel.id
        const lists = panel.tabs ?? []
        const nested = lists.length > 1
        return (
          <div key={panel.id}>
            <button
              type="button"
              aria-expanded={nested ? open : undefined}
              aria-current={selected ? 'true' : undefined}
              data-tracking="ignore"
              onClick={() => selectPanel(panel)}
              className={tw(
                'flex min-h-[44px] w-full cursor-pointer items-center justify-between gap-2 rounded-[10px] border-0 bg-transparent px-3 py-2 text-left text-[16px] leading-snug transition-colors',
                selected ? 'font-semibold text-(--sh-ink)' : 'font-medium text-(--sh-ink-muted) hover:text-(--sh-ink)',
                selected && (!nested || !open) && 'bg-(--sh-hover)'
              )}
            >
              {panel.title}
              {nested ? (
                <SvgChevronDown
                  aria-hidden
                  fill="currentColor"
                  className={tw('size-3 shrink-0 text-(--sh-ink-soft) transition-transform', !open && '-rotate-90')}
                />
              ) : null}
            </button>
            {open && nested ? (
              <div
                role="tablist"
                aria-orientation="vertical"
                aria-label={panel.title}
                className="mb-1 flex flex-col gap-0.5 pl-4"
              >
                {lists.map((tab) => {
                  const active = tab.id === activeTabId
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={active}
                      data-tracking="ignore"
                      onClick={() => onSelectTab(tab)}
                      className={tw(
                        'flex min-h-[40px] w-full cursor-pointer items-center rounded-[10px] border-0 bg-transparent px-3 py-2 text-left text-[15px] leading-snug transition-colors',
                        active
                          ? 'bg-(--sh-hover) font-semibold text-(--sh-ink)'
                          : 'font-medium text-(--sh-ink-muted) hover:text-(--sh-ink)'
                      )}
                    >
                      {tab.title}
                    </button>
                  )
                })}
              </div>
            ) : null}
          </div>
        )
      })}
    </nav>
  )
}
